import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { 
  FileText, Plus, Edit, Trash2, Download, FolderOpen, 
  Calendar, Eye, Filter, Building2, Pencil, Check, X, Copy, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CVList = () => {
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [cvs, setCVs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [exportId, setExportId] = useState(null);
  const [exportLang, setExportLang] = useState('pt');
  const [previewId, setPreviewId] = useState(null);
  const [previewLang, setPreviewLang] = useState('pt');
  const [previewing, setPreviewing] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  
  // Filters
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [filterPeriod, setFilterPeriod] = useState('all');
  
  // Inline edit
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');

  // Admin-only "Ver por utilizador" filter. Values:
  //   'mine' → default, only current user's CVs
  //   'all'  → every CV in the platform
  //   <user_id> → CVs owned by that user
  const isAdmin = user?.role === 'admin';
  const [ownerFilter, setOwnerFilter] = useState('mine');
  const [platformUsers, setPlatformUsers] = useState([]);

  useEffect(() => {
    fetchCVs();
    fetchClients();
    if (isAdmin) fetchPlatformUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch CVs whenever the admin switches the owner filter.
  useEffect(() => {
    if (!isAdmin) return;
    fetchCVs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  const fetchCVs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (isAdmin && ownerFilter && ownerFilter !== 'mine') {
        params.owner_id = ownerFilter; // 'all' or a specific user_id
      }
      const response = await axios.get(`${API}/cvs`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setCVs(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatformUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching platform users:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Filter CVs
  const filteredCVs = cvs.filter(cv => {
    if (filterTemplate !== 'all' && cv.template !== filterTemplate) return false;
    if (filterClient !== 'all' && cv.client_id !== filterClient) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const nameMatch = (cv.name || '').toLowerCase().includes(query);
      const idMatch = (cv.id || '').toLowerCase().includes(query);
      if (!nameMatch && !idMatch) return false;
    }
    // Period filter
    if (filterPeriod !== 'all' && cv.updated_at) {
      const cvDate = new Date(cv.updated_at);
      const now = new Date();
      const diffDays = (now - cvDate) / (1000 * 60 * 60 * 24);
      if (filterPeriod === 'today' && diffDays > 1) return false;
      if (filterPeriod === '1week' && diffDays > 7) return false;
      if (filterPeriod === '15days' && diffDays > 15) return false;
      if (filterPeriod === '1month' && diffDays > 30) return false;
    }
    return true;
  }).sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at || 0);
    const dateB = new Date(b.updated_at || b.created_at || 0);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/cvs/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('deleteSuccess'));
      fetchCVs();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setDeleteId(null);
    }
  };

  const handleUpdateName = async (cvId) => {
    if (!editingNameValue.trim()) {
      toast.error(language === 'pt' ? 'O nome não pode estar vazio' : 'Name cannot be empty');
      return;
    }
    
    try {
      const cv = cvs.find(c => c.id === cvId);
      await axios.put(`${API}/cvs/${cvId}`, {
        ...cv,
        name: editingNameValue.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'pt' ? 'Nome atualizado com sucesso!' : 'Name updated successfully!');
      setEditingNameId(null);
      setEditingNameValue('');
      fetchCVs();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const startEditingName = (cv) => {
    setEditingNameId(cv.id);
    setEditingNameValue(cv.name || '');
  };

  const cancelEditingName = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };

  const handleDuplicate = async (cv) => {
    setDuplicating(cv.id);
    try {
      // Create a copy of the CV with a new name
      const duplicatedCV = {
        ...cv,
        name: `${cv.name} (${language === 'pt' ? 'Cópia' : 'Copy'})`,
      };
      
      // Remove fields that shouldn't be duplicated
      delete duplicatedCV.id;
      delete duplicatedCV.created_at;
      delete duplicatedCV.updated_at;
      delete duplicatedCV.user_id;
      
      await axios.post(`${API}/cvs`, duplicatedCV, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'pt' ? 'CV duplicado com sucesso!' : 'CV duplicated successfully!');
      fetchCVs();
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setDuplicating(null);
    }
  };

  const handlePreview = async (cvId, lang) => {
    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/cvs/${cvId}/preview/${lang}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setPreviewId(null);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleExport = async (cvId, lang) => {
    try {
      // Direct browser navigation to a Content-Disposition: attachment URL.
      // This is the most reliable cross-browser download method (works in
      // Safari, iOS Safari, Chrome, Firefox) because it does NOT rely on JS
      // blobs / <a download> / fetch — the browser does a plain GET and the
      // server tells it "this is a file, save it".
      const url = `${API}/cvs/${cvId}/download/${lang}?token=${encodeURIComponent(token)}`;
      // Use a hidden iframe so the current page stays in place. If the server
      // returns Content-Disposition: attachment (it does), the iframe never
      // navigates — the browser downloads the file instead.
      const ifr = document.createElement('iframe');
      ifr.style.display = 'none';
      ifr.src = url;
      document.body.appendChild(ifr);
      setTimeout(() => ifr.remove(), 60000);
      setExportId(null);
      toast.success(language === 'pt' ? 'CV exportado com sucesso!' : 'CV exported successfully!');
    } catch (error) {
      // Show the REAL error so users (and the dev team) can debug — silent
      // failures used to confuse non-technical users into thinking "nada
      // acontece".
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || 'unknown';
      const msg = status
        ? (language === 'pt'
          ? `Erro ao exportar (HTTP ${status}): ${detail}`
          : `Export failed (HTTP ${status}): ${detail}`)
        : (language === 'pt'
          ? `Erro ao exportar: ${detail}`
          : `Export failed: ${detail}`);
      console.error('CV export failed:', { cvId, lang, status, detail, error });
      toast.error(msg, { duration: 12000 });
    }
  };

  const handleQuickExport = async (cvId, format) => {
    try {
      const lang = language || 'pt';
      let endpoint, ext, mime;
      if (format === 'pptx') {
        endpoint = `${API}/cvs/${cvId}/export-pptx/${lang}`;
        ext = 'pptx';
        mime = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      } else {
        endpoint = `${API}/cvs/${cvId}/export/${lang}`;
        ext = 'docx';
        mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      }
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const cv = cvs.find(c => c.id === cvId);
      const blob = new Blob([response.data], { type: mime });
      const safeName = (cv?.name || 'document')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9_-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'document';
      saveAs(blob, `CV_${safeName}_${lang.toUpperCase()}.${ext}`);
      toast.success(language === 'pt' ? 'CV exportado!' : 'CV exported!');
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message || 'unknown';
      const msg = status
        ? (language === 'pt'
          ? `Erro ao exportar (HTTP ${status}): ${detail}`
          : `Export failed (HTTP ${status}): ${detail}`)
        : (language === 'pt'
          ? `Erro ao exportar: ${detail}`
          : `Export failed: ${detail}`);
      console.error('Quick CV export failed:', { cvId, format, status, detail, error });
      toast.error(msg, { duration: 12000 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8 px-4" data-testid="cv-list-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('myCVs')}
            </h1>
            <p className="text-[#64748b]">
              {filteredCVs.length} {language === 'pt' ? 'de' : 'of'} {cvs.length} {language === 'pt' ? 'currículos' : 'CVs'}
            </p>
          </div>
          <Button
            className="btn-primary"
            onClick={() => navigate('/cv/new')}
            data-testid="create-cv-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('createCV')}
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6" data-testid="filters-section">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-[#1E3C3C]" />
            <span className="font-medium text-[#0f172a]">{language === 'pt' ? 'Filtros' : 'Filters'}</span>
          </div>
          
          {/* Search Field */}
          <div className="mb-4">
            <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Pesquisar por Nome/ID' : 'Search by Name/ID'}</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748b]" />
              <Input
                type="text"
                placeholder={language === 'pt' ? 'Pesquisar por nome ou ID do CV...' : 'Search by CV name or ID...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Ordenar' : 'Sort'}</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="mt-1" data-testid="sort-order">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{language === 'pt' ? 'Mais recentes' : 'Newest first'}</SelectItem>
                  <SelectItem value="oldest">{language === 'pt' ? 'Mais antigos' : 'Oldest first'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Período' : 'Period'}</Label>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="mt-1" data-testid="filter-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'pt' ? 'Todos' : 'All'}</SelectItem>
                  <SelectItem value="today">{language === 'pt' ? 'Hoje' : 'Today'}</SelectItem>
                  <SelectItem value="1week">{language === 'pt' ? '1 Semana' : '1 Week'}</SelectItem>
                  <SelectItem value="15days">{language === 'pt' ? '15 Dias' : '15 Days'}</SelectItem>
                  <SelectItem value="1month">{language === 'pt' ? '1 Mês' : '1 Month'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Template' : 'Template'}</Label>
              <Select value={filterTemplate} onValueChange={setFilterTemplate}>
                <SelectTrigger className="mt-1" data-testid="filter-template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'pt' ? 'Todos' : 'All'}</SelectItem>
                  <SelectItem value="outsourcing">Outsourcing</SelectItem>
                  <SelectItem value="perm">Perm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Cliente' : 'Client'}</Label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="mt-1" data-testid="filter-client">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'pt' ? 'Todos' : 'All'}</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label className="text-sm text-[#64748b]">{language === 'pt' ? 'Utilizador' : 'User'}</Label>
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="mt-1" data-testid="filter-owner">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mine">{language === 'pt' ? 'Os meus CVs' : 'My CVs'}</SelectItem>
                    <SelectItem value="all">{language === 'pt' ? 'Todos os utilizadores' : 'All users'}</SelectItem>
                    {platformUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email} {u.email && u.name ? `(${u.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(filterTemplate !== 'all' || filterClient !== 'all' || searchQuery.trim() || filterPeriod !== 'all' || sortOrder !== 'newest' || (isAdmin && ownerFilter !== 'mine')) && (
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setFilterTemplate('all'); setFilterClient('all'); setSearchQuery(''); setFilterPeriod('all'); setSortOrder('newest'); if (isAdmin) setOwnerFilter('mine'); }}
                  className="text-[#64748b]"
                  data-testid="clear-filters-btn"
                >
                  {language === 'pt' ? 'Limpar filtros' : 'Clear filters'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {filteredCVs.length === 0 ? (
          <Card className="text-center py-16" data-testid="empty-state">
            <CardContent>
              <div className="w-16 h-16 rounded-full bg-[#f0fdfa] flex items-center justify-center mx-auto mb-4">
                <FolderOpen className="w-8 h-8 text-[#1E3C3C]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0f172a] mb-2">
                {t('noCVs')}
              </h3>
              <p className="text-[#64748b] mb-6">{t('createFirst')}</p>
              <Button
                className="btn-primary"
                onClick={() => navigate('/cv/new')}
                data-testid="create-first-cv-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('createCV')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCVs.map((cv) => (
              <Card
                key={cv.id}
                className="card-hover border-2 border-transparent hover:border-[#1E3C3C]/20 group"
                data-testid={`cv-card-${cv.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      cv.template === 'outsourcing' ? 'bg-[#1E3C3C]' : 'bg-[#FFD700]'
                    }`}>
                      <FileText className={`w-6 h-6 ${cv.template === 'outsourcing' ? 'text-white' : 'text-[#1a202c]'}`} />
                    </div>
                    <span className={`badge-${cv.template === 'outsourcing' ? 'primary' : 'secondary'}`}>
                      {cv.template === 'outsourcing' ? 'Outsourcing' : 'Perm'}
                    </span>
                  </div>

                  {isAdmin && ownerFilter !== 'mine' && (cv.owner_name || cv.owner_email) && (
                    <div
                      className="text-xs text-[#1E3C3C] bg-[#1E3C3C]/8 border border-[#1E3C3C]/20 rounded px-2 py-1 mb-2 inline-flex items-center gap-1 max-w-full truncate"
                      data-testid={`cv-owner-${cv.id}`}
                      title={cv.owner_email || ''}
                    >
                      <span className="font-medium">{language === 'pt' ? 'Autor:' : 'Owner:'}</span>
                      <span className="truncate">{cv.owner_name || cv.owner_email}</span>
                    </div>
                  )}

                  {editingNameId === cv.id ? (
                    <div className="flex items-center gap-2 mb-2">
                      <Input
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        className="h-8 text-sm"
                        placeholder={language === 'pt' ? 'Nome/ID' : 'Name/ID'}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateName(cv.id);
                          if (e.key === 'Escape') cancelEditingName();
                        }}
                        data-testid={`edit-name-input-${cv.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                        onClick={() => handleUpdateName(cv.id)}
                        data-testid={`save-name-${cv.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                        onClick={cancelEditingName}
                        data-testid={`cancel-name-${cv.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-1 group/name">
                      <h3 className="font-semibold text-[#0f172a] truncate flex-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {cv.name || 'Sem nome'}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover/name:opacity-100 transition-opacity text-[#64748b] hover:text-[#1E3C3C]"
                        onClick={() => startEditingName(cv)}
                        title={language === 'pt' ? 'Editar nome/ID' : 'Edit name/ID'}
                        data-testid={`quick-edit-name-${cv.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Display CV ID */}
                  <p className="text-xs text-[#94a3b8] mb-2 font-mono truncate" title={cv.id} data-testid={`cv-id-display-${cv.id}`}>
                    ID: {cv.id}
                  </p>
                  
                  {cv.client_name && (
                    <p className="text-sm text-[#1E3C3C] mb-2 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />
                      {cv.client_name}
                    </p>
                  )}
                  
                  {cv.technical_test_result !== null && cv.technical_test_result !== undefined && (
                    <div className="mb-2">
                      <div className="flex items-center gap-2 text-xs text-[#64748b]">
                        <span>{language === 'pt' ? 'Teste Técnico' : 'Technical Test'}:</span>
                        <span className="font-semibold text-[#1E3C3C]">
                          {cv.technical_test_result}%
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-[#64748b] mb-4 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {language === 'pt' ? 'Última atualização' : 'Last update'}: {new Date(cv.updated_at).toLocaleDateString('pt-PT')}
                  </p>

                  <div className="space-y-2">
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="flex-1 text-[#1E3C3C] border-[#1E3C3C] hover:bg-[#f0fdfa] text-xs px-2"
                        onClick={() => navigate(`/cv/new?id=${cv.id}`)} data-testid={`edit-cv-${cv.id}`}>
                        <Edit className="w-3.5 h-3.5 mr-1" />{language === 'pt' ? 'Editar' : 'Edit'}
                      </Button>
                      {/* PDF preview button hidden (Feb 2026 user spec) — only
                          Word export is exposed; PDF preview can be re-enabled
                          when the layout is fully validated against templates. */}
                      <Button variant="outline" size="sm" className="text-xs px-2"
                        onClick={() => { setExportLang(cv.cv_language || 'pt'); setExportId(cv.id); }} data-testid={`export-cv-${cv.id}`}>
                        Word
                      </Button>
                    </div>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="sm" className="text-blue-500 border-blue-200 hover:bg-blue-50 text-xs px-2"
                        onClick={() => handleDuplicate(cv)} disabled={duplicating === cv.id} data-testid={`duplicate-cv-${cv.id}`}>
                        {duplicating === cv.id ? <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                        {language === 'pt' ? 'Duplicar' : 'Duplicate'}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 text-xs px-2"
                        onClick={() => setDeleteId(cv.id)} data-testid={`delete-cv-${cv.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" />{language === 'pt' ? 'Eliminar' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar eliminação</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser revertida. O CV será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <AlertDialog open={!!previewId} onOpenChange={() => setPreviewId(null)}>
        <AlertDialogContent data-testid="preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pré-visualizar CV</AlertDialogTitle>
            <AlertDialogDescription>
              Escolha o idioma para pré-visualizar o CV
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={previewLang} onValueChange={setPreviewLang}>
              <SelectTrigger data-testid="preview-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">Português</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-preview-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handlePreview(previewId, previewLang)}
              className="btn-primary"
              disabled={previewing}
              data-testid="confirm-preview-btn"
            >
              {previewing ? <div className="loading-spinner w-4 h-4 mr-2"></div> : <Eye className="w-4 h-4 mr-2" />}
              Pré-visualizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <AlertDialog open={!!exportId} onOpenChange={() => setExportId(null)}>
        <AlertDialogContent data-testid="export-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Exportar Word</AlertDialogTitle>
            <AlertDialogDescription>
              Escolha o idioma para exportar o CV
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={exportLang} onValueChange={setExportLang}>
              <SelectTrigger data-testid="export-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pt">Português</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="de">Deutsch</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-export-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleExport(exportId, exportLang)}
              className="btn-primary"
              data-testid="confirm-export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
